import { useQuery } from '@apollo/client';
import { AccountStates } from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import { useInnerWorkflow } from '@ovComponents/4-module/workflowCompletion/components/useInnerWorkflow';
import type { WorkflowCompletionWithId } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionWithId.types';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { appendUpdatedAccount } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { Box, Skeleton, Typography, useToast } from 'hephaestus/web';
import { groupBy, uniq } from 'lodash-es';
import { type ComponentProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { AccountCard } from './accountCard';
import { FETCH_ACCOUNTS_FOR_REVIEW } from './reviewAccount.queries';

export type ReviewAccountSubstepOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  createAnotherAccount?: boolean;
  enableAccountEdit?: boolean;
  enableAccountDelete?: boolean;
  accountEditWorkflow?: 'default' | string;
  displayAccountType?: boolean;
  displayCustodian?: boolean;
  displayRepcode?: boolean;
  displayRiskTolerence?: boolean;
  displayAffiliates?: boolean;
  displayLinkedGoal?: boolean;
  displayCustomFields?: boolean;
  enableCallToActions?: boolean;
  callToActions?: unknown[];
};

export const ReviewAccountVisual = ({
  options,
  userId,
  workflowCompletion,
}: {
  options: ReviewAccountSubstepOptions;
  userId?: string;
  workflowCompletion: WorkflowCompletionWithId;
}) => {
  const { translateBackendString } = useLocalization();
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { data, loading, refetch } = useQuery(
    FETCH_ACCOUNTS_FOR_REVIEW as any,
    {
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          pagination: { perPage: 100 },
          filter: {
            userId: workflowData.currentUserId || userId,
            states: [
              AccountStates.Active,
              AccountStates.Requested,
              AccountStates.Frozen,
              AccountStates.Initiated,
              AccountStates.Ready,
            ],
          },
        },
      },
    },
  );

  const { t } = useTranslation([
    'workflowCompletions',
    'accountTypes',
    'affiliationTypes',
  ]);

  const { createWorkflowCompletion, innerWorkflowModal } = useInnerWorkflow({
    refetch,
    workflowCompletion,
  });
  const currentUserId = workflowData.currentUserId || userId;
  const { enqueueToast } = useToast();
  const onAccountEdit: ComponentProps<typeof AccountCard>['onAccountEdit'] =
    async (accountId) => {
      if (
        !options.accountEditWorkflow ||
        options.accountEditWorkflow === 'default'
      ) {
        enqueueToast(t('reviewAccount.noWorkflowErrorMessage'), {
          variant: 'error',
        });
        return;
      }
      await createWorkflowCompletion({
        currentUserId,
        currentAccountId: accountId,
        workflowId: options.accountEditWorkflow,
        workflowContext: {
          ...workflowData,
          currentAccountId: accountId,
        },
        onWorkflowCompleted: async (completedContext) => {
          const childUpdatedAccounts = completedContext?.updatedAccounts;
          if (!childUpdatedAccounts?.length) return;

          await setWorkflowData((prev) => {
            let next = prev;
            for (const account of childUpdatedAccounts) {
              next = appendUpdatedAccount(next, account);
            }
            return next;
          });
        },
      });
    };

  const accountsById = groupBy(data?.fetchAccounts?.accounts, 'id');

  const accountsFromWorkflow = uniq([
    workflowData?.currentAccountId,
    ...(workflowData?.createdAccounts?.map((a) => a.id) || []),
  ])
    .map((id: string | undefined) => accountsById[id || '']?.[0])
    .filter((account) => !!account);

  const accountsToDisplay = accountsFromWorkflow.length
    ? accountsFromWorkflow
    : (data?.fetchAccounts?.accounts ?? []);

  return (
    <Box>
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackendString(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        {translateBackendString(options?.subtitle)}
      </Typography>

      {loading ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
        </>
      ) : (
        accountsToDisplay?.map((account: any) => (
          <AccountCard
            key={account.id}
            account={account}
            allAccounts={accountsToDisplay}
            options={options}
            onAccountDeleted={() => refetch()}
            onAccountEdit={onAccountEdit}
          />
        ))
      )}
      {innerWorkflowModal}
    </Box>
  );
};
